import type { ReactNode } from "react";
import type { DatasetMode } from "../types";

type Group = "models" | "settings";

const GROUPS: { id: Group; label: string }[] = [
  { id: "models", label: "Models" },
  { id: "settings", label: "Map settings" },
];

const Tag = ({ children, warn = false }: { children: ReactNode; warn?: boolean }) => (
  <span
    className={
      warn
        ? "rounded-full bg-amber-50 px-2 py-0.5 text-2xs font-medium text-amber-800"
        : "rounded-full bg-zinc-100 px-2 py-0.5 text-2xs font-medium text-zinc-600"
    }
  >
    {children}
  </span>
);

const listText = (names: string[]): string =>
  names.length < 3
    ? names.join(" and ")
    : `${names.slice(0, -1).join(", ")}, and ${names.at(-1)}`;

/** The note of a row when all modes give the same one, else null. */
const sharedNote = (modes: DatasetMode[], group: Group, label: string): string | null => {
  const notes = modes.map(
    (mode) => (mode[group] ?? []).find((setting) => setting.label === label)?.note ?? null,
  );
  return notes.every((note) => note && note === notes[0]) ? notes[0] : null;
};

/** The modes that use a workflow side by side, with their models and the settings of their maps. */
export const ModeSettingsTable = ({ modes }: { modes: DatasetMode[] }) => {
  const names = listText(modes.map((mode) => mode.label));
  const labelsOf = (group: Group) => [
    ...new Set(modes.flatMap((mode) => (mode[group] ?? []).map((s) => s.label))),
  ];
  return (
    <section aria-labelledby="workflow-modes">
      <h3 id="workflow-modes" className="text-base font-semibold text-zinc-900">
        Modes
      </h3>
      <p className="mt-1 text-sm text-zinc-600">
        {modes.length === 1
          ? `The ${names} uses this workflow.`
          : `The ${names} use this workflow.`}{" "}
        Each mode sets the model of each agent of the run, and adds these settings to its
        maps.
      </p>
      <div
        role="region"
        aria-label="Mode settings"
        tabIndex={0}
        className="mt-3 overflow-x-auto rounded-lg border border-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
      >
        <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
          <caption className="sr-only">
            Models and settings of the modes that use this workflow
          </caption>
          <thead className="bg-zinc-50">
            <tr>
              <th
                scope="col"
                className="w-48 px-4 py-3 align-top text-2xs font-semibold uppercase tracking-wide text-zinc-500"
              >
                Setting
              </th>
              {modes.map((mode) => (
                <th key={mode.id} scope="col" className="px-4 py-3 align-top font-normal">
                  <span className="block text-sm font-semibold text-zinc-900">{mode.label}</span>
                  <span className="mt-1 flex flex-wrap gap-1">
                    {mode.default && <Tag>Default</Tag>}
                    {mode.gate && <Tag>{mode.gate}</Tag>}
                    {!mode.available && <Tag warn>Not available on this server</Tag>}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-zinc-600">
                    {mode.summary}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          {GROUPS.map((group) => {
            const labels = labelsOf(group.id);
            if (labels.length === 0) return null;
            return (
              <tbody
                key={group.id}
                className="divide-y divide-zinc-200 border-t border-zinc-200"
              >
                <tr className="bg-zinc-50">
                  <th
                    scope="colgroup"
                    colSpan={modes.length + 1}
                    className="px-4 py-2 text-2xs font-semibold uppercase tracking-wide text-zinc-500"
                  >
                    {group.label}
                  </th>
                </tr>
                {labels.map((label) => {
                  const note = sharedNote(modes, group.id, label);
                  return (
                    <tr key={label}>
                      <th
                        scope="row"
                        className="px-4 py-3 align-top text-xs font-semibold text-zinc-700"
                      >
                        {label}
                        {note && (
                          <span className="mt-1 block text-2xs font-normal leading-4 text-zinc-500">
                            {note}
                          </span>
                        )}
                      </th>
                      {modes.map((mode) => {
                        const setting = (mode[group.id] ?? []).find(
                          (item) => item.label === label,
                        );
                        return (
                          <td key={mode.id} className="px-4 py-3 align-top text-zinc-900">
                            {setting?.value ?? "Not used"}
                            {!note && setting?.note && (
                              <span className="mt-1 block text-xs leading-5 text-zinc-500">
                                {setting.note}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            );
          })}
        </table>
      </div>
      {modes
        .filter((mode) => mode.choices.length > 0)
        .map((mode) => (
          <div key={mode.id} className="mt-4 rounded-lg border border-zinc-200 bg-white p-4">
            <h4 className="text-sm font-semibold text-zinc-900">
              What the user can change in the {mode.label}
            </h4>
            <p className="mt-0.5 text-xs text-zinc-500">
              The values in the table apply when the user changes nothing.
            </p>
            <dl className="mt-3 space-y-2">
              {mode.choices.map((choice) => (
                <div key={choice.label} className="sm:flex sm:gap-3">
                  <dt className="text-xs font-medium text-zinc-700 sm:w-48 sm:shrink-0">
                    {choice.label}
                  </dt>
                  <dd className="text-xs leading-5 text-zinc-600">{choice.options.join(", ")}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
    </section>
  );
};
